import React, { useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { useHistory, useParams } from 'react-router-dom'

import cogoToast from 'cogo-toast'

import CaseTypesService from '../../services/CaseTypesService'
import ReportTypesService from '../../services/ReportTypesService'

const CaseTypeEdit = ({ user }) => {
  const { id } = useParams()
  const history = useHistory()
  const isNew = !id

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [reportTypes, setReportTypes] = useState([])
  const [form, setForm] = useState({
    name: '',
    description: '',
    allowedReportTypes: '',
    autoFiledReportTypes: '',
    isActive: true,
    key: '',
    lineOfInsurance: '',
  })

  // Derived: which keys are currently checked in AllowedReportTypes
  const allowedKeys = form.allowedReportTypes
    ? form.allowedReportTypes
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    : []

  useEffect(() => {
    const rtPromise = ReportTypesService.getAll().then(setReportTypes)

    const caseTypePromise = isNew
      ? Promise.resolve()
      : CaseTypesService.getById(parseInt(id, 10)).then(ct => {
          if (ct) setForm(ct)
        })

    Promise.all([rtPromise, caseTypePromise])
      .then(() => setIsLoading(false))
      .catch(() => {
        cogoToast.error('Failed to load data.')
        setIsLoading(false)
      })
    // eslint-disable-next-line
  }, [id])

  function toggleAllowed(key) {
    let newAllowed
    if (allowedKeys.includes(key)) {
      newAllowed = allowedKeys.filter(k => k !== key)
      // Also remove from auto-filed if no longer allowed
      const autoFiled = form.autoFiledReportTypes
        ? form.autoFiledReportTypes
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        : []
      const newAutoFiled = autoFiled.filter(k => newAllowed.includes(k))
      setForm(f => ({
        ...f,
        allowedReportTypes: newAllowed.join(','),
        autoFiledReportTypes: newAutoFiled.join(','),
      }))
    } else {
      newAllowed = [...allowedKeys, key]
      setForm(f => ({ ...f, allowedReportTypes: newAllowed.join(',') }))
    }
  }

  function toggleAutoFiled(key) {
    const autoFiled = form.autoFiledReportTypes
      ? form.autoFiledReportTypes
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      : []
    const newAutoFiled = autoFiled.includes(key) ? autoFiled.filter(k => k !== key) : [...autoFiled, key]
    setForm(f => ({ ...f, autoFiledReportTypes: newAutoFiled.join(',') }))
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) {
      cogoToast.error('Name is required.')
      return
    }
    if (isNew && !form.key.trim()) {
      cogoToast.error('Key is required for new case types.')
      return
    }
    if (isNew && !/^[A-Za-z0-9]+$/.test(form.key)) {
      cogoToast.error('Key must contain only letters and numbers, no spaces or special characters.')
      return
    }

    setIsSaving(true)
    const action = isNew ? CaseTypesService.create(form) : CaseTypesService.update(parseInt(id, 10), form)

    action
      .then(() => {
        cogoToast.success(isNew ? 'Case type created.' : 'Case type updated.')
        history.push('/library/case-types')
      })
      .catch(() => {
        cogoToast.error('Failed to save case type.')
        setIsSaving(false)
      })
  }

  function handleDeactivate() {
    if (!window.confirm('Deactivate this case type? It will no longer appear in the intake form.')) return
    CaseTypesService.deactivate(parseInt(id, 10))
      .then(() => {
        cogoToast.success('Case type deactivated.')
        history.push('/library/case-types')
      })
      .catch(() => cogoToast.error('Failed to deactivate.'))
  }

  if (isLoading)
    return (
      <div className="container mt-4">
        <p>Loading...</p>
      </div>
    )

  return (
    <div>
      <Helmet title={`${isNew ? 'New' : 'Edit'} Case Type | Library`} />
      <div className="container mt-4" style={{ maxWidth: 720 }}>
        <h3 className="mb-4">{isNew ? 'New Case Type' : 'Edit Case Type'}</h3>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="ct-name">Name*</label>
            <input
              id="ct-name"
              name="name"
              type="text"
              className="form-control"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="ct-description">Description</label>
            <textarea
              id="ct-description"
              name="description"
              className="form-control"
              rows={3}
              value={form.description || ''}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="ct-key">
              Key* {!isNew && <span className="badge badge-secondary ml-1">read-only</span>}
            </label>
            <input
              id="ct-key"
              name="key"
              type="text"
              className="form-control"
              value={form.key || ''}
              onChange={handleChange}
              readOnly={!isNew}
              required={isNew}
              placeholder="e.g. ColoradoRule16"
            />
            <small className="form-text text-muted">
              A short alphanumeric identifier used internally (no spaces or special characters). Cannot be changed after
              the case type is created.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="ct-line-of-insurance">Line of Insurance</label>
            <input
              id="ct-line-of-insurance"
              name="lineOfInsurance"
              type="text"
              className="form-control"
              value={form.lineOfInsurance || ''}
              onChange={handleChange}
              placeholder="e.g. Work Comp"
            />
            <small className="form-text text-muted">Displayed on the Face Sheet. Leave blank if not applicable.</small>
          </div>

          <div className="form-group">
            <label>Allowed Report Types</label>
            {reportTypes.length === 0 && <p className="text-muted small">No report types available.</p>}
            <div className="border rounded p-3">
              {reportTypes.map(rt => (
                <div key={rt.key} className="form-check">
                  <input
                    id={`allowed-${rt.key}`}
                    type="checkbox"
                    className="form-check-input"
                    checked={allowedKeys.includes(rt.key)}
                    onChange={() => toggleAllowed(rt.key)}
                  />
                  <label className="form-check-label" htmlFor={`allowed-${rt.key}`}>
                    {rt.displayName}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {allowedKeys.length > 0 && (
            <div className="form-group">
              <label>Auto-Filed Report Types</label>
              <p className="text-muted small">
                These report types will bypass the approval queue and be filed automatically. Only report types checked
                above can be auto-filed.
              </p>
              <div className="border rounded p-3">
                {reportTypes
                  .filter(rt => allowedKeys.includes(rt.key))
                  .map(rt => {
                    const autoFiled = form.autoFiledReportTypes
                      ? form.autoFiledReportTypes
                          .split(',')
                          .map(s => s.trim())
                          .filter(Boolean)
                      : []
                    return (
                      <div key={rt.key} className="form-check">
                        <input
                          id={`autofiled-${rt.key}`}
                          type="checkbox"
                          className="form-check-input"
                          checked={autoFiled.includes(rt.key)}
                          onChange={() => toggleAutoFiled(rt.key)}
                        />
                        <label className="form-check-label" htmlFor={`autofiled-${rt.key}`}>
                          {rt.displayName}
                        </label>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}

          {!isNew && (
            <div className="form-group">
              <div className="form-check">
                <input
                  id="ct-isActive"
                  name="isActive"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.isActive}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="ct-isActive">
                  Active
                </label>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-between mt-4">
            <div>
              <button id="saveCaseTypeBtn" type="submit" className="btn btn-primary mr-2" disabled={isSaving}>
                {isSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => history.push('/library/case-types')}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
            {!isNew && form.isActive && (
              <button
                id="deactivateCaseTypeBtn"
                type="button"
                className="btn btn-outline-danger"
                onClick={handleDeactivate}
              >
                Deactivate
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default CaseTypeEdit
